import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import UserLinksEditor from '../components/UserLinksEditor';
import api from '../api';
import { normalizeIban, ibanHint } from '../lib/iban';
import { signupInterestLabel } from '../lib/signupInterest';
import { MAX_LEVEL, levelFromRole, roleLabel as levelRoleLabel } from '../lib/levels';

// Super-admin vidí všetky schvaľovacie úrovne; koľko ich firma reálne používa
// si nastavuje sama v Admin → Pravidlá.
const APPROVER_LEVELS = Array.from({ length: MAX_LEVEL }, (_, i) => i + 1);

const API = '/api';
const BASE = window.location.hostname === 'localhost' ? 'https://schvalovanie.mgtech.sk' : '';

type Tab = 'general' | 'email' | 'slack' | 'users' | 'logo' | 'features' | 'flowii' | 'omega' | 'billing' | 'activity' | 'audit' | 'danger';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  ico: string | null;
  dic: string | null;
  ic_dph: string | null;
  address_street: string | null;
  address_city: string | null;
  address_zip: string | null;
  address_country: string | null;
  phone: string | null;
  contact_email: string | null;
  website: string | null;
  iban: string | null;
  business_register: string | null;
  notes: string | null;
  active: boolean;
  signup_source?: { zaujem?: string } | null;
  billing_plan: string;
  billing_model: string;
  max_documents_per_month: number | null;
  price_per_document?: string | number | null;
  email_inbox: string | null;
  imap_host: string | null;
  imap_port: number | null;
  imap_user: string | null;
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_user: string | null;
  smtp_from: string | null;
  smtp_reply_to: string | null;
  smtp_secure: boolean;
  accounting_email: string | null;
  logo_url: string | null;
  has_slack_bot_token: boolean;
  has_telegram?: boolean;
  telegram_chat_id?: string | null;
  features: Record<string, boolean>;
  peppol_customer_ref?: string | null;
  peppol_consent?: any;
  has_flowii_api_key?: boolean;
  has_flowii_password?: boolean;
  flowii_username?: string | null;
  flowii_company_id?: string | null;
  flowii_base_url?: string | null;
  billing_cap_override?: boolean;
  created_at: string;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  last_login?: string | null;
  slack_user_id?: string;
  telegram_chat_id?: string;
  notify_summary?: boolean;
  notify_new_document?: boolean;
  notify_issue?: boolean;
  notify_via_email?: boolean;
  notify_via_slack?: boolean;
  notify_via_telegram?: boolean;
  notify_via_push?: boolean;
}

export default function TenantEdit() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (slug) loadTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Ak je aktívny tab skrytý (feature vypnutý, napr. po zmene vo Features)
  // → prepni na Základné údaje, aby sa nezobrazoval obsah skrytého tabu.
  useEffect(() => {
    if (!tenant) return;
    if (activeTab === 'flowii' && !tenant.features?.flowii_integration) setActiveTab('general');
    if (activeTab === 'omega' && !tenant.features?.omega_export) setActiveTab('general');
  }, [tenant, activeTab]);

  const loadTenant = async () => {
    try {
      const res = await api.get(`/auth/tenant/${slug}`);
      setTenant(res.data);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba pri načítaní firmy');
      navigate('/superadmin');
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (m: string) => {
    setMessage(m);
    setTimeout(() => setMessage(''), 3000);
  };

  if (loading || !tenant) return <div style={{ padding: '2rem' }}>Načítavam...</div>;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
      {/* Header */}
      <div style={{ backgroundColor: 'white', padding: '1rem 2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => navigate('/superadmin')} style={{ padding: '0.4rem 0.8rem', background: 'none', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', color: '#374151' }}>
            ← Späť
          </button>
          <div>
            <h1 style={{ margin: 0, color: '#1f2937', fontSize: '1.15rem' }}>{tenant.name}</h1>
            <span style={{ color: '#6b7280', fontSize: '0.8rem', fontFamily: 'monospace' }}>{tenant.slug}</span>
            {!tenant.active && <span style={{ marginLeft: '0.5rem', padding: '0.15rem 0.5rem', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600 }}>DEAKTIVOVANÁ</span>}
            {signupInterestLabel(tenant.signup_source) && <span style={{ marginLeft: '0.5rem', padding: '0.15rem 0.5rem', backgroundColor: '#dbeafe', color: '#1d4ed8', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600 }}>{signupInterestLabel(tenant.signup_source)}</span>}
          </div>
        </div>
        {message && (
          <span style={{ padding: '0.4rem 0.8rem', backgroundColor: message.startsWith('✓') ? '#dcfce7' : '#fee2e2', color: message.startsWith('✓') ? '#16a34a' : '#dc2626', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 500 }}>
            {message}
          </span>
        )}
      </div>

      {/* Tabs */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', padding: '0 2rem' }}>
        {(['general', 'email', 'slack', 'users', 'logo', 'features', 'flowii', 'omega', 'billing', 'activity', 'audit', 'danger'] as Tab[])
          .filter(tab =>
            tab === 'flowii' ? !!tenant?.features?.flowii_integration :
            tab === 'omega'  ? !!tenant?.features?.omega_export :
            true)
          .map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={tabStyle(activeTab === tab, tab === 'danger')}>
            {tabLabel(tab)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '2rem' }}>
        {activeTab === 'general' && <GeneralTab tenant={tenant} onUpdated={(t) => { setTenant(t); showMsg('✓ Údaje uložené'); }} />}
        {activeTab === 'email' && <EmailTab tenant={tenant} onUpdated={loadTenant} showMsg={showMsg} />}
        {activeTab === 'slack' && <SlackTab tenant={tenant} onUpdated={loadTenant} showMsg={showMsg} />}
        {activeTab === 'users' && <UsersTab tenantId={tenant.id} />}
        {activeTab === 'logo' && <LogoTab tenant={tenant} onUploaded={loadTenant} headers={headers} />}
        {activeTab === 'features' && <FeaturesTab tenant={tenant} onUpdated={(features) => { setTenant({ ...tenant, features }); showMsg('✓ Features uložené'); }} />}
        {activeTab === 'flowii' && <FlowiiTab tenant={tenant} onUpdated={loadTenant} showMsg={showMsg} />}
        {activeTab === 'omega' && <OmegaTab tenant={tenant} onUpdated={loadTenant} showMsg={showMsg} />}
        {activeTab === 'billing' && <BillingTab tenant={tenant} onUpdated={(t) => { setTenant(t); showMsg('✓ Billing uložený'); }} />}
        {activeTab === 'activity' && <ActivityTab slug={tenant.slug} />}
        {activeTab === 'audit' && <AuditTab slug={tenant.slug} />}
        {activeTab === 'danger' && <DangerTab tenant={tenant} navigate={navigate} />}
      </div>
    </div>
  );
}

// ============================================================
// TAB: Základné údaje
// ============================================================
function GeneralTab({ tenant, onUpdated }: { tenant: Tenant; onUpdated: (t: Tenant) => void }) {
  const [form, setForm] = useState({
    name: tenant.name,
    slug: tenant.slug,
    ico: tenant.ico || '',
    dic: tenant.dic || '',
    ic_dph: tenant.ic_dph || '',
    address_street: tenant.address_street || '',
    address_city: tenant.address_city || '',
    address_zip: tenant.address_zip || '',
    address_country: tenant.address_country || 'SK',
    phone: tenant.phone || '',
    contact_email: tenant.contact_email || '',
    website: tenant.website || '',
    iban: tenant.iban || '',
    business_register: tenant.business_register || '',
    notes: tenant.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupMsg, setLookupMsg] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[] | null>(null);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  const lookupCompany = async () => {
    if (!form.ico || form.ico.length < 6) {
      setLookupMsg('✗ Zadaj IČO (6-8 číslic)');
      return;
    }
    setLookingUp(true);
    setLookupMsg('');
    try {
      const res = await api.get(`/auth/company-lookup/${form.ico}`);
      const d = res.data;
      // Vyplň len prázdne polia, existujúce hodnoty nepriepisuj
      setForm(f => ({
        ...f,
        name: f.name || d.name || '',
        dic: f.dic || d.dic || '',
        ic_dph: f.ic_dph || d.ic_dph || '',
        address_street: f.address_street || d.address_street || '',
        address_city: f.address_city || d.address_city || '',
        address_zip: f.address_zip || d.address_zip || '',
        address_country: f.address_country || d.address_country || 'SK',
      }));
      const src = d.source || 'RPO/RÚZ';
      const vat = d.ic_dph_valid === true ? ', IČ DPH overené VIES'
        : d.ic_dph_valid === false ? ', ⚠ IČ DPH NEoverené VIES' : '';
      setLookupMsg(`✓ Načítané z ${src}${vat} (prázdne polia doplnené)`);
    } catch (err: any) {
      setLookupMsg('✗ ' + (err.response?.data?.error || 'Chyba pri načítaní'));
    } finally {
      setLookingUp(false);
      setTimeout(() => setLookupMsg(''), 5000);
    }
  };

  // Vyhľadanie firmy podľa NÁZVU (alebo IČO) cez RPO — vráti kandidátov
  const searchCompany = async () => {
    const q = searchQuery.trim();
    if (q.length < 3) {
      setLookupMsg('✗ Zadaj aspoň 3 znaky (názov firmy alebo IČO)');
      setTimeout(() => setLookupMsg(''), 4000);
      return;
    }
    setSearching(true);
    setSearchResults(null);
    setLookupMsg('');
    try {
      const res = await api.get(`/auth/company-search`, { params: { q } });
      setSearchResults(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      setLookupMsg('✗ ' + (err.response?.data?.error || 'Chyba pri vyhľadávaní'));
      setTimeout(() => setLookupMsg(''), 4000);
    } finally {
      setSearching(false);
    }
  };

  // Výber kandidáta zo zoznamu → prepíš názov/IČO/adresu (user ho zvolil)
  // a dotiahni DIČ/IČ DPH cez agregát (RÚZ + VIES overenie).
  const pickCandidate = async (c: any) => {
    setForm(f => ({
      ...f,
      name: c.name || f.name,
      ico: c.ico || f.ico,
      address_street: c.address_street || f.address_street,
      address_city: c.address_city || f.address_city,
      address_zip: c.address_zip || f.address_zip,
      address_country: c.address_country || f.address_country || 'SK',
    }));
    setSearchResults(null);
    setSearchQuery('');
    if (!c.ico) return;
    setLookingUp(true);
    setLookupMsg('');
    try {
      const res = await api.get(`/auth/company-lookup/${c.ico}`);
      const d = res.data;
      setForm(f => ({
        ...f,
        dic: d.dic || f.dic || '',
        ic_dph: d.ic_dph || f.ic_dph || '',
      }));
      const vat = d.ic_dph_valid === true ? 'IČ DPH overené VIES'
        : d.ic_dph_valid === false ? '⚠ IČ DPH NEoverené VIES' : 'DIČ doplnené';
      setLookupMsg(`✓ ${c.name} načítaná (RPO + ${vat})`);
    } catch (err: any) {
      setLookupMsg('✓ Názov a adresa načítané z RPO (DIČ sa nepodarilo)');
    } finally {
      setLookingUp(false);
      setTimeout(() => setLookupMsg(''), 5000);
    }
  };

  const save = async () => {
    if (form.slug !== tenant.slug) {
      if (!window.confirm('Meníš slug firmy. Pozor: zmena slugu môže rozbiť uložené odkazy a Slack tlačidlá. Pokračovať?')) return;
    }
    setSaving(true);
    try {
      const res = await api.put(`/auth/tenant/${tenant.slug}`, form);
      if (res.data.slug && res.data.slug !== tenant.slug) {
        // Slug sa zmenil — preroutuj na novú URL
        navigate(`/superadmin/tenant/${res.data.slug}`, { replace: true });
      } else {
        onUpdated({ ...tenant, ...form });
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba pri ukladaní');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Základné údaje firmy">
      <div style={grid2}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl}>Vyhľadať firmu (názov alebo IČO)</label>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchCompany(); } }}
              placeholder="napr. WESTech, spol. s r.o.  alebo  35796111"
            />
            <button type="button" onClick={searchCompany}
              disabled={searching || searchQuery.trim().length < 3}
              style={{ ...btnNeutral, whiteSpace: 'nowrap', opacity: (searching || searchQuery.trim().length < 3) ? 0.6 : 1 }}>
              {searching ? 'Hľadám…' : '🔍 Hľadať firmu'}
            </button>
          </div>
          {searchResults && (
            <div style={{ marginTop: '0.4rem', border: '1px solid #e5e7eb', borderRadius: 6, maxHeight: 220, overflowY: 'auto' }}>
              {searchResults.length === 0 ? (
                <p style={{ margin: 0, padding: '0.5rem 0.6rem', fontSize: '0.8rem', color: '#6b7280' }}>Žiadna firma nenájdená</p>
              ) : searchResults.map((c, i) => (
                <button key={c.ico || i} type="button" onClick={() => pickCandidate(c)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '0.45rem 0.6rem',
                    border: 'none', borderBottom: i < searchResults.length - 1 ? '1px solid #f1f5f9' : 'none',
                    background: '#fff', cursor: 'pointer', fontSize: '0.82rem',
                  }}>
                  <strong>{c.name}</strong> <span style={{ color: '#6b7280' }}>· IČO {c.ico}</span>
                  <br />
                  <span style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                    {[c.address_street, c.address_zip, c.address_city].filter(Boolean).join(', ')}
                  </span>
                </button>
              ))}
            </div>
          )}
          <p style={{ margin: '0.3rem 0 0', fontSize: '0.72rem', color: '#9ca3af' }}>
            Zdroj: RPO (Štatistický úrad SR) — aktuálne dáta. Po výbere sa dotiahne DIČ (RÚZ) + overí IČ DPH (VIES).
          </p>
        </div>
        <Field label="Názov firmy" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
        <Field label="Slug (URL)" value={form.slug} onChange={v => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, '') })} hint="Zmena slugu môže rozbiť uložené odkazy" />
        <div>
          <label style={lbl}>IČO</label>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <input style={{ ...inputStyle, flex: 1 }} value={form.ico} onChange={e => setForm({ ...form, ico: e.target.value.replace(/\s/g, '') })} />
            <button type="button" onClick={lookupCompany} disabled={lookingUp || !form.ico} style={{ ...btnNeutral, whiteSpace: 'nowrap', opacity: (lookingUp || !form.ico) ? 0.6 : 1 }}>
              {lookingUp ? 'Hľadám...' : '🔍 Načítať (RPO/RÚZ)'}
            </button>
          </div>
          {lookupMsg && <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: lookupMsg.startsWith('✓') ? '#16a34a' : '#dc2626' }}>{lookupMsg}</p>}
        </div>
        <Field label="DIČ" value={form.dic} onChange={v => setForm({ ...form, dic: v })} />
        <Field label="IBAN" value={form.iban}
          onChange={v => setForm({ ...form, iban: normalizeIban(v) })}
          hint={ibanHint(form.iban)} placeholder="SK0000000000000000000000" />
        <Field label="IČ DPH" value={form.ic_dph} onChange={v => setForm({ ...form, ic_dph: v })} />
        <Field label="Telefón" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
        <Field label="Kontaktný email" value={form.contact_email} onChange={v => setForm({ ...form, contact_email: v })} type="email" />
        <Field label="Web" value={form.website} onChange={v => setForm({ ...form, website: v })} />
      </div>
      {/* §3b Obchodného zákonníka — registrový zápis patrí na faktúru */}
      <Field label="Zápis v obchodnom registri (na faktúre)"
        value={form.business_register}
        onChange={v => setForm({ ...form, business_register: v })}
        placeholder="napr. OR OS Žilina, oddiel Sro, vložka č. 12345/L" />

      <h3 style={h3}>Adresa</h3>
      <div style={grid2}>
        <Field label="Ulica a č." value={form.address_street} onChange={v => setForm({ ...form, address_street: v })} />
        <Field label="Mesto" value={form.address_city} onChange={v => setForm({ ...form, address_city: v })} />
        <Field label="PSČ" value={form.address_zip} onChange={v => setForm({ ...form, address_zip: v })} />
        <Field label="Krajina" value={form.address_country} onChange={v => setForm({ ...form, address_country: v })} />
      </div>

      <h3 style={h3}>Poznámky</h3>
      <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
        style={{ ...inputStyle, minHeight: '80px', fontFamily: 'inherit' }} placeholder="Interné poznámky..." />

      <div style={{ marginTop: '1.5rem' }}>
        <button onClick={save} disabled={saving} style={{ ...btnPrimary, padding: '0.6rem 1.5rem' }}>
          {saving ? 'Ukladám...' : 'Uložiť zmeny'}
        </button>
      </div>
    </Card>
  );
}

// ============================================================
// TAB: Email
// ============================================================
function EmailTab({ tenant, onUpdated, showMsg }: { tenant: Tenant; onUpdated: () => void; showMsg: (m: string) => void }) {
  const [form, setForm] = useState({
    imapHost: tenant.imap_host || '',
    imapPort: String(tenant.imap_port || 993),
    imapUser: tenant.imap_user || '',
    imapPassword: '',
    emailInbox: tenant.email_inbox || '',
    imapIssuedUser: (tenant as any).imap_issued_user || '',
    imapIssuedPassword: '',
    smtpHost: tenant.smtp_host || '',
    smtpPort: String(tenant.smtp_port || 587),
    smtpUser: tenant.smtp_user || '',
    smtpPassword: '',
    smtpFrom: tenant.smtp_from || '',
    smtpReplyTo: tenant.smtp_reply_to || '',
    smtpSecure: tenant.smtp_secure || false,
    accountingEmail: tenant.accounting_email || '',
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ imap: { ok: boolean; messages?: number; error?: string } | null; imap_issued?: { ok: boolean; messages?: number; error?: string } | null; smtp: { ok: boolean; error?: string } | null } | null>(null);
  const token = localStorage.getItem('token');

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/auth/test-email-connection`, {
        tenantSlug: tenant.slug,
        imapHost: form.imapHost, imapPort: parseInt(form.imapPort),
        imapUser: form.imapUser, imapPassword: form.imapPassword,
        emailInbox: form.emailInbox,
        imapIssuedUser: form.imapIssuedUser, imapIssuedPassword: form.imapIssuedPassword,
        smtpHost: form.smtpHost, smtpPort: parseInt(form.smtpPort),
        smtpUser: form.smtpUser, smtpPassword: form.smtpPassword,
        smtpSecure: form.smtpSecure,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setTestResult(res.data);
    } catch (err: any) {
      showMsg('✗ ' + (err.response?.data?.error || 'Test zlyhal'));
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/auth/setup-email`, {
        tenantSlug: tenant.slug,
        imapHost: form.imapHost, imapPort: parseInt(form.imapPort),
        imapUser: form.imapUser, imapPassword: form.imapPassword,
        emailInbox: form.emailInbox,
        imapIssuedUser: form.imapIssuedUser, imapIssuedPassword: form.imapIssuedPassword,
        smtpHost: form.smtpHost, smtpPort: parseInt(form.smtpPort),
        smtpUser: form.smtpUser, smtpPassword: form.smtpPassword,
        smtpFrom: form.smtpFrom, smtpSecure: form.smtpSecure,
        smtpReplyTo: form.smtpReplyTo,
        accountingEmail: form.accountingEmail,
      }, { headers: { Authorization: `Bearer ${token}` } });
      showMsg('✓ Email nastavenia uložené');
      onUpdated();
    } catch (err: any) {
      showMsg('✗ ' + (err.response?.data?.error || 'Chyba'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Email integrácia">
      <h3 style={h3}>📥 IMAP — Príjem dokladov (Schvaľovanie faktúr)</h3>
      <p style={{ margin: '0 0 0.6rem', fontSize: '0.78rem', color: '#6b7280' }}>
        Hlavná schránka na došlé doklady — spracujú sa len PDF prílohy (obrázky dostanú
        automatickú odpoveď s prosbou o PDF). Prázdny používateľ = schránka vypnutá.
      </p>
      <div style={grid2}>
        <Field label="IMAP server" value={form.imapHost} onChange={v => setForm({ ...form, imapHost: v })} placeholder="imap.firma.sk" />
        <Field label="Port" value={form.imapPort} onChange={v => setForm({ ...form, imapPort: v })} type="number" />
        <Field label="Používateľ" value={form.imapUser} onChange={v => setForm({ ...form, imapUser: v })} placeholder="faktury@firma.sk" />
        <Field label="Heslo (prázdne = nezmení existujúce)" value={form.imapPassword} onChange={v => setForm({ ...form, imapPassword: v })} type="password" />
        <Field label="Email inbox (folder)" value={form.emailInbox} onChange={v => setForm({ ...form, emailInbox: v })} placeholder="INBOX" />
      </div>

      <h3 style={h3}>📥 IMAP — Import vystavených faktúr do Fakturácie</h3>
      <p style={{ margin: '0 0 0.6rem', fontSize: '0.78rem', color: '#6b7280' }}>
        Druhá schránka na tom istom IMAP serveri — klient sem posiela PDF s vystavenými
        faktúrami zo svojho fakturačného systému (jeden PDF môže obsahovať viac faktúr).
        Prázdny používateľ = schránka vypnutá.
      </p>
      <div style={grid2}>
        <Field label="Používateľ" value={form.imapIssuedUser} onChange={v => setForm({ ...form, imapIssuedUser: v })} placeholder="fakturacia@firma.sk" />
        <Field label="Heslo (prázdne = nezmení existujúce)" value={form.imapIssuedPassword} onChange={v => setForm({ ...form, imapIssuedPassword: v })} type="password" />
      </div>

      <h3 style={h3}>📤 SMTP — Odoslanie notifikácií</h3>
      <div style={grid2}>
        <Field label="SMTP server" value={form.smtpHost} onChange={v => setForm({ ...form, smtpHost: v })} placeholder="smtp.firma.sk" />
        <Field label="Port" value={form.smtpPort} onChange={v => setForm({ ...form, smtpPort: v })} type="number" />
        <Field label="Používateľ" value={form.smtpUser} onChange={v => setForm({ ...form, smtpUser: v })} />
        <Field label="Heslo (prázdne = nezmení existujúce)" value={form.smtpPassword} onChange={v => setForm({ ...form, smtpPassword: v })} type="password" />
        <Field label="From adresa" value={form.smtpFrom} onChange={v => setForm({ ...form, smtpFrom: v })} placeholder='"Firma" <faktury@firma.sk>' fullWidth />
        <Field label="Reply-To (kam prídu odpovede)" value={form.smtpReplyTo} onChange={v => setForm({ ...form, smtpReplyTo: v })} placeholder="meno@firma.sk" fullWidth />
        <p style={{ gridColumn: '1 / -1', margin: '-0.35rem 0 0', fontSize: '0.78rem', color: '#6b7280' }}>
          Doplní sa do každého odchádzajúceho emailu (faktúry, notifikácie). Bez nej odpoveď príjemcu padne do From schránky — často automatu, kde si ju nikto nevšimne. Prázdne = bez hlavičky.
        </p>
        <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type="checkbox" checked={form.smtpSecure} onChange={e => setForm({ ...form, smtpSecure: e.target.checked })} />
          <label style={{ fontSize: '0.85rem', color: '#374151' }}>SSL (port 465) — vypni pre STARTTLS (port 587)</label>
        </div>
      </div>

      <h3 style={h3}>📨 Doručovanie schválených faktúr</h3>
      <Field label="Email na doručovanie schválených faktúr" value={form.accountingEmail} onChange={v => setForm({ ...form, accountingEmail: v })} placeholder="uctovnik@firma.sk" />
      {!form.accountingEmail?.trim() && (
        <p style={{ margin: '0.4rem 0 0', padding: '0.5rem 0.75rem', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, fontSize: '0.8rem' }}>
          ⚠ Nie je nastavená schránka — <strong>schválené doklady sa nikam neodosielajú</strong>. Doplň email účtovníčky/účtovníctva.
        </p>
      )}

      <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button onClick={save} disabled={saving} style={{ ...btnPrimary, padding: '0.6rem 1.5rem' }}>
          {saving ? 'Ukladám...' : 'Uložiť email nastavenia'}
        </button>
        <button onClick={testConnection} disabled={testing || (!form.imapHost && !form.smtpHost)}
          style={{ padding: '0.6rem 1.5rem', background: '#fff', color: '#2563eb', border: '1px solid #2563eb', borderRadius: 6, cursor: (testing || (!form.imapHost && !form.smtpHost)) ? 'default' : 'pointer', fontWeight: 600, opacity: (testing || (!form.imapHost && !form.smtpHost)) ? 0.6 : 1 }}>
          {testing ? 'Testujem…' : '🔌 Otestovať pripojenie'}
        </button>
      </div>
      {testResult && (
        <div style={{ marginTop: '0.9rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {testResult.imap && (
            <div style={{ padding: '0.6rem 0.9rem', borderRadius: 6, fontSize: '0.85rem', background: testResult.imap.ok ? '#f0fdf4' : '#fef2f2', color: testResult.imap.ok ? '#166534' : '#b91c1c', border: `1px solid ${testResult.imap.ok ? '#bbf7d0' : '#fecaca'}` }}>
              {testResult.imap.ok
                ? `✓ IMAP pripojený — v schránke ${testResult.imap.messages ?? 0} správ.`
                : `✗ IMAP zlyhal: ${testResult.imap.error}`}
            </div>
          )}
          {testResult.imap_issued && (
            <div style={{ padding: '0.6rem 0.9rem', borderRadius: 6, fontSize: '0.85rem', background: testResult.imap_issued.ok ? '#f0fdf4' : '#fef2f2', color: testResult.imap_issued.ok ? '#166534' : '#b91c1c', border: `1px solid ${testResult.imap_issued.ok ? '#bbf7d0' : '#fecaca'}` }}>
              {testResult.imap_issued.ok
                ? `✓ IMAP (import vystavených) pripojený — v schránke ${testResult.imap_issued.messages ?? 0} správ.`
                : `✗ IMAP (import vystavených) zlyhal: ${testResult.imap_issued.error}`}
            </div>
          )}
          {testResult.smtp && (
            <div style={{ padding: '0.6rem 0.9rem', borderRadius: 6, fontSize: '0.85rem', background: testResult.smtp.ok ? '#f0fdf4' : '#fef2f2', color: testResult.smtp.ok ? '#166534' : '#b91c1c', border: `1px solid ${testResult.smtp.ok ? '#bbf7d0' : '#fecaca'}` }}>
              {testResult.smtp.ok
                ? '✓ SMTP pripojený — odosielanie funguje.'
                : `✗ SMTP zlyhal: ${testResult.smtp.error}`}
            </div>
          )}
          <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>
            Test používa heslá z formulára; ak sú prázdne, použije uložené. Pri zmene hesla najprv ulož.
          </p>
        </div>
      )}
    </Card>
  );
}

// ============================================================
// TAB: Slack
// ============================================================
function SlackTab({ tenant, onUpdated, showMsg }: { tenant: Tenant; onUpdated: () => void; showMsg: (m: string) => void }) {
  const [token, setToken] = useState('');
  const [tgToken, setTgToken] = useState('');
  const [tgChat, setTgChat] = useState(tenant.telegram_chat_id || '');
  const [saving, setSaving] = useState(false);
  const authToken = localStorage.getItem('token');

  const post = async (extra: Record<string, any>, okMsg: string) => {
    setSaving(true);
    try {
      await axios.post(`${API}/auth/setup-email`, {
        tenantSlug: tenant.slug,
        imapHost: tenant.imap_host, imapPort: tenant.imap_port,
        imapUser: tenant.imap_user, imapPassword: '',
        emailInbox: tenant.email_inbox,
        smtpHost: tenant.smtp_host, smtpPort: tenant.smtp_port,
        smtpUser: tenant.smtp_user, smtpPassword: '',
        smtpFrom: tenant.smtp_from, smtpSecure: tenant.smtp_secure,
        smtpReplyTo: tenant.smtp_reply_to,
        accountingEmail: tenant.accounting_email,
        slackBotToken: '',
        ...extra,
      }, { headers: { Authorization: `Bearer ${authToken}` } });
      showMsg(okMsg);
      onUpdated();
    } catch (err: any) {
      showMsg('✗ ' + (err.response?.data?.error || 'Chyba'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card title="Slack integrácia">
        <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '1rem' }}>
          Bot musí mať scopy: <code>chat:write</code>, <code>im:write</code>, <code>users:read.email</code>. Notifikácie sa posielajú schvaľovateľom danej úrovne, adminom a pri zamietnutí aj odosielateľovi.
        </p>
        <Field
          label="Slack Bot Token (xoxb-...)"
          value={token}
          onChange={setToken}
          type="password"
          placeholder={tenant.has_slack_bot_token ? '••••••• (nastavený — zadaj nový pre zmenu)' : 'xoxb-...'}
        />
        <p style={{ margin: '0.25rem 0 1.5rem', fontSize: '0.75rem', color: tenant.has_slack_bot_token ? '#16a34a' : '#9ca3af' }}>
          {tenant.has_slack_bot_token ? '✓ Token je nastavený' : 'Token ešte nie je nastavený'}
        </p>
        <button onClick={() => { post({ slackBotToken: token }, '✓ Slack token uložený'); setToken(''); }}
          disabled={saving || !token} style={{ ...btnPrimary, padding: '0.6rem 1.5rem', opacity: (saving || !token) ? 0.6 : 1 }}>
          {saving ? 'Ukladám...' : 'Uložiť Slack token'}
        </button>
      </Card>

      <Card title="Telegram integrácia">
        <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '1rem' }}>
          Vytvor bota cez <code>@BotFather</code> → získaš <strong>bot token</strong>.
          Pridaj bota do skupiny/chatu a zisti <strong>chat ID</strong> (napr. cez <code>@userinfobot</code> alebo
          <code>getUpdates</code>). Notifikácie sa posielajú do tohto chatu.
        </p>
        <Field
          label="Telegram Bot Token"
          value={tgToken}
          onChange={setTgToken}
          type="password"
          placeholder={tenant.has_telegram ? '••••••• (nastavený — zadaj nový pre zmenu)' : '123456:ABC-...'}
        />
        <Field
          label="Telegram Chat ID"
          value={tgChat}
          onChange={setTgChat}
          placeholder="napr. -1001234567890 alebo 123456789"
        />
        <p style={{ margin: '0.25rem 0 1.5rem', fontSize: '0.75rem', color: tenant.has_telegram ? '#16a34a' : '#9ca3af' }}>
          {tenant.has_telegram ? '✓ Telegram je nastavený' : 'Telegram ešte nie je nastavený'}
        </p>
        <button onClick={() => { post({ telegramBotToken: tgToken, telegramChatId: tgChat }, '✓ Telegram nastavenia uložené'); setTgToken(''); }}
          disabled={saving || (!tgToken && !tgChat)} style={{ ...btnPrimary, padding: '0.6rem 1.5rem', opacity: (saving || (!tgToken && !tgChat)) ? 0.6 : 1 }}>
          {saving ? 'Ukladám...' : 'Uložiť Telegram'}
        </button>
      </Card>
    </>
  );
}

// ============================================================
// TAB: Používatelia
// ============================================================
function UsersTab({ tenantId }: { tenantId: string }) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', email: '', role: '', password: '', slack_user_id: '', telegram_chat_id: '', notify_summary: true, notify_new_document: true, notify_issue: true, notify_via_email: false, notify_via_slack: true, notify_via_telegram: false, notify_via_push: true });
  const [editSaving, setEditSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ first_name: '', last_name: '', email: '', role: 'approver_l1', password: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tenantId]);

  const load = async () => {
    try {
      const res = await api.get(`/users/tenant/${tenantId}`);
      setUsers(res.data);
    } catch (e) {
      console.error('Load users failed');
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#';
    let pwd = '';
    for (let i = 0; i < 12; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
    return pwd;
  };

  const startEdit = (u: User) => {
    setEditing(u);
    setEditForm({
      first_name: u.first_name, last_name: u.last_name, email: u.email, role: u.role, password: '',
      slack_user_id: u.slack_user_id || '', telegram_chat_id: u.telegram_chat_id || '',
      notify_summary: u.notify_summary ?? true, notify_new_document: u.notify_new_document ?? true, notify_issue: u.notify_issue ?? true,
      notify_via_email: u.notify_via_email ?? false, notify_via_slack: u.notify_via_slack ?? true, notify_via_telegram: u.notify_via_telegram ?? false, notify_via_push: u.notify_via_push ?? true,
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditSaving(true);
    try {
      await api.put(`/users/tenant/${tenantId}/${editing.id}`, editForm);
      setEditing(null);
      load();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    } finally {
      setEditSaving(false);
    }
  };

  const addUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/users/tenant/${tenantId}`, newUser);
      setNewUser({ first_name: '', last_name: '', email: '', role: 'approver_l1', password: '' });
      setShowAdd(false);
      load();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    }
  };

  if (loading) return <div>Načítavam...</div>;

  return (
    <Card title={`Používatelia (${users.length})`}>
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={() => setShowAdd(true)} style={btnPrimary}>+ Pridať používateľa</button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f9fafb' }}>
            <th style={th}>Meno</th>
            <th style={th}>Email</th>
            <th style={th}>Rola</th>
            <th style={th}>Posledné prihlásenie</th>
            <th style={{ ...th, textAlign: 'right' }}>Akcia</th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <React.Fragment key={u.id}>
              <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                <td style={td}>{u.first_name} {u.last_name}</td>
                <td style={td}>{u.email}</td>
                <td style={td}>{roleLabel(u.role)}</td>
                <td style={{ ...td, color: '#6b7280', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                  {u.last_login ? new Date(u.last_login).toLocaleString('sk-SK') : '—'}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <button onClick={() => startEdit(u)} style={btnPrimary}>Editovať</button>
                </td>
              </tr>
              {editing?.id === u.id && (
                <tr style={{ backgroundColor: '#eff6ff' }}>
                  <td colSpan={5} style={{ padding: '1rem' }}>
                    <div style={grid2}>
                      <Field label="Meno" value={editForm.first_name} onChange={v => setEditForm({ ...editForm, first_name: v })} />
                      <Field label="Priezvisko" value={editForm.last_name} onChange={v => setEditForm({ ...editForm, last_name: v })} />
                      <Field label="Email" value={editForm.email} onChange={v => setEditForm({ ...editForm, email: v })} type="email" />
                      <div>
                        <label style={lbl}>Rola</label>
                        <select style={inputStyle} value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })}>
                          {APPROVER_LEVELS.map(n => (
                            <option key={n} value={`approver_l${n}`}>Schvaľovateľ L{n}</option>
                          ))}
                          <option value="accountant">Účtovník</option>
                          <option value="admin">Admin</option>
                          <option value="submitter">Zberač dokladov (iba nahráva)</option>
                          <option value="viewer">Pozorovateľ</option>
                        </select>
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={lbl}>Nové heslo (voliteľné)</label>
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          <input style={{ ...inputStyle, flex: 1 }} type="text" value={editForm.password} onChange={e => setEditForm({ ...editForm, password: e.target.value })} placeholder="prázdne = bez zmeny" />
                          <button type="button" onClick={() => setEditForm({ ...editForm, password: generatePassword() })} style={btnNeutral}>Generovať</button>
                        </div>
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={lbl}>Slack User ID (voliteľné)</label>
                        <input style={inputStyle} placeholder="U0123456789" value={editForm.slack_user_id} onChange={e => setEditForm({ ...editForm, slack_user_id: e.target.value })} />
                      </div>
                      <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Notifikácie</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div>
                            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem' }}>Čo dostávať</div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', marginBottom: '0.3rem', cursor: 'pointer' }}>
                              <input type="checkbox" checked={editForm.notify_summary} onChange={e => setEditForm({ ...editForm, notify_summary: e.target.checked })} /> Ranný sumár (8:30)
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', marginBottom: '0.3rem', cursor: 'pointer' }}>
                              <input type="checkbox" checked={editForm.notify_new_document} onChange={e => setEditForm({ ...editForm, notify_new_document: e.target.checked })} /> Nový doklad na mojej úrovni
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                              <input type="checkbox" checked={editForm.notify_issue} onChange={e => setEditForm({ ...editForm, notify_issue: e.target.checked })} /> Nespracovaný doklad / chyba
                            </label>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem' }}>Kam doručiť</div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', marginBottom: '0.3rem', cursor: 'pointer' }}>
                              <input type="checkbox" checked={editForm.notify_via_email} onChange={e => setEditForm({ ...editForm, notify_via_email: e.target.checked })} /> Email
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', marginBottom: '0.3rem', cursor: 'pointer' }}>
                              <input type="checkbox" checked={editForm.notify_via_slack} onChange={e => setEditForm({ ...editForm, notify_via_slack: e.target.checked })} /> Slack
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', marginBottom: '0.4rem', cursor: 'pointer' }}>
                              <input type="checkbox" checked={editForm.notify_via_telegram} onChange={e => setEditForm({ ...editForm, notify_via_telegram: e.target.checked })} /> Telegram
                            </label>
                            {editForm.notify_via_telegram && (
                              <input style={inputStyle} placeholder="Telegram chat ID" value={editForm.telegram_chat_id} onChange={e => setEditForm({ ...editForm, telegram_chat_id: e.target.value })} />
                            )}
                            {/* Push chýbal len tu — firemný admin (Admin.tsx) ho mal, server tiež. */}
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', marginBottom: '0.3rem', cursor: 'pointer' }}
                              title="Push do PWA appky. Doručuje sa len na zariadenia, kde si používateľ notifikácie sám zapol (zvonček v appke).">
                              <input type="checkbox" checked={editForm.notify_via_push} onChange={e => setEditForm({ ...editForm, notify_via_push: e.target.checked })} /> Push (mobil)
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div style={{ marginTop: '0.75rem' }}>
                      <UserLinksEditor userId={u.id} />
                    </div>
                    <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                      <button onClick={saveEdit} disabled={editSaving} style={{ ...btnPrimary, padding: '0.5rem 1.2rem' }}>
                        {editSaving ? 'Ukladám...' : 'Uložiť'}
                      </button>
                      <button onClick={() => setEditing(null)} style={btnNeutral}>Zrušiť</button>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {showAdd && (
        <div onClick={() => setShowAdd(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', width: '100%', maxWidth: '500px' }}>
            <h2 style={{ margin: '0 0 1rem' }}>Pridať používateľa</h2>
            <form onSubmit={addUser} style={grid2}>
              <Field label="Meno" value={newUser.first_name} onChange={v => setNewUser({ ...newUser, first_name: v })} required />
              <Field label="Priezvisko" value={newUser.last_name} onChange={v => setNewUser({ ...newUser, last_name: v })} required />
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Email" value={newUser.email} onChange={v => setNewUser({ ...newUser, email: v })} type="email" required />
              </div>
              <div>
                <label style={lbl}>Rola</label>
                <select style={inputStyle} value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                  {APPROVER_LEVELS.map(n => (
                    <option key={n} value={`approver_l${n}`}>Schvaľovateľ L{n}</option>
                  ))}
                  <option value="accountant">Účtovník</option>
                  <option value="admin">Admin</option>
                  <option value="submitter">Zberač dokladov (iba nahráva)</option>
                  <option value="viewer">Pozorovateľ</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Heslo</label>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <input style={{ ...inputStyle, flex: 1 }} type="text" required value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
                  <button type="button" onClick={() => setNewUser({ ...newUser, password: generatePassword() })} style={btnNeutral}>Gen.</button>
                </div>
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAdd(false)} style={btnNeutral}>Zrušiť</button>
                <button type="submit" style={btnPrimary}>Vytvoriť</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Card>
  );
}

// ============================================================
// TAB: Logo
// ============================================================
function LogoTab({ tenant, onUploaded, headers }: { tenant: Tenant; onUploaded: () => void; headers: any }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('logo', file);
    try {
      await axios.post(`${API}/auth/upload-logo/${tenant.id}`, formData, { headers });
      onUploaded();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba pri uploade loga');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card title="Logo firmy">
      <div style={{ marginBottom: '1rem' }}>
        {tenant.logo_url ? (
          <div style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '4px', display: 'inline-block' }}>
            <img src={`${BASE}${tenant.logo_url}`} alt="logo" style={{ maxHeight: '100px', maxWidth: '300px', objectFit: 'contain' }} />
          </div>
        ) : (
          <p style={{ color: '#9ca3af' }}>Žiadne logo nie je nastavené.</p>
        )}
      </div>
      <label style={{ display: 'inline-block', padding: '0.6rem 1.2rem', backgroundColor: '#2563eb', color: 'white', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem' }}>
        {uploading ? 'Nahrávam...' : (tenant.logo_url ? 'Zmeniť logo' : 'Nahrať logo')}
        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} disabled={uploading} />
      </label>
      <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#9ca3af' }}>PNG, JPG. Optimálne 200x100px.</p>
    </Card>
  );
}

// ============================================================
// TAB: Features (feature flagy per tenant)
// ============================================================
const FEATURE_DEFS: { key: string; label: string; description: string }[] = [
  {
    key: 'resale_detection',
    label: 'Resale Detection',
    description: 'Automatická klasifikácia položiek na nákladových faktúrach (preposlané klientovi vs interný náklad). Zdroj výstupných faktúr: Fakturácia, FLOWii alebo Pohoda import.',
  },
  {
    key: 'flowii_integration',
    label: 'FLOWii Integration',
    description: 'Sync výstupných faktúr z FLOWii — slúži ako zdroj klientov pre matching.',
  },
  {
    key: 'omega_export',
    label: 'Omega Export',
    description: 'Modul Účtovníctvo — Omega TXT exporty (EUD/T01), číselník partnerov T04 a mesačný ZIP balík pre účtovníčku vrátane odoslania emailom.',
  },
  {
    key: 'invoicing',
    label: 'Fakturácia',
    description: 'Modul Fakturácia — natívne vystavovanie faktúr, dobropisov a zálohových faktúr priamo v systéme.',
  },
  {
    key: 'orders',
    label: 'Objednávky',
    description: 'Modul Objednávky — interné objednávky so schvaľovaním, PDF a prepojením na došlé faktúry.',
  },
  {
    key: 'finance_overview',
    label: 'Financie',
    description: 'Modul Financie — koľko má prísť z vystavených faktúr a kedy, odhad DPH za obdobie a odhad dane z príjmu. Pre admina a účtovníka. Odhady vychádzajú len z dokladov v systéme.',
  },
  {
    key: 'peppol',
    label: 'E-fakturácia (Peppol)',
    description: 'Digitálny poštár (ePošťák): došlé e-faktúry idú priamo do schvaľovania (bez OCR, s originálnym XML), vystavené faktúry sa dajú odoslať cez Peppol a dodávateľ dostane odpoveď o schválení/zamietnutí. Povinné pre platiteľov DPH od 1. 1. 2027. Vyžaduje customerRef firmy schválenej u poštára.',
  },
];

function FeaturesTab({ tenant, onUpdated }: { tenant: Tenant; onUpdated: (features: Record<string, boolean>) => void }) {
  const [features, setFeatures] = useState<Record<string, boolean>>(tenant.features || {});
  const [saving, setSaving] = useState(false);
  // customerRef firmy u digitálneho poštára — nastaví integrátor (my) v paneli
  // ePošťáka po schválení firmy; tu ho len prepojíme s tenantom.
  const [peppolRef, setPeppolRef] = useState(tenant.peppol_customer_ref || '');
  // Súhlas firmy u poštára (consent offer): my vytvoríme pozvánku, konateľ ju
  // otvorí a potvrdí u poštára; stav sa overuje na požiadanie (bez callbacku).
  const [consent, setConsent] = useState<any>(tenant.peppol_consent || null);
  const [consentBusy, setConsentBusy] = useState(false);
  const createConsent = async () => {
    if (consent?.status === 'accepted' && !window.confirm('Firma už má súhlas udelený. Vytvoriť novú pozvánku (starú nahradí)?')) return;
    setConsentBusy(true);
    try {
      const r = await api.post(`/auth/tenant/${tenant.slug}/peppol/consent`);
      setConsent(r.data); tenant.peppol_consent = r.data;
    } catch (err: any) { alert(err.response?.data?.error || 'Pozvánku sa nepodarilo vytvoriť'); }
    finally { setConsentBusy(false); }
  };
  const refreshConsent = async () => {
    setConsentBusy(true);
    try {
      const r = await api.get(`/auth/tenant/${tenant.slug}/peppol/consent`);
      setConsent(r.data); tenant.peppol_consent = r.data;
    } catch (err: any) { alert(err.response?.data?.error || 'Stav sa nepodarilo overiť'); }
    finally { setConsentBusy(false); }
  };
  const CONSENT_LABELS: Record<string, { label: string; color: string; bg: string }> = {
    issued: { label: 'pozvánka vystavená — čaká na konateľa', color: '#92400e', bg: '#fef3c7' },
    pending: { label: 'čaká na potvrdenie', color: '#92400e', bg: '#fef3c7' },
    accepted: { label: 'súhlas udelený', color: '#166534', bg: '#dcfce7' },
    claimed: { label: 'prevzatá', color: '#1e40af', bg: '#dbeafe' },
    revoked: { label: 'súhlas odvolaný', color: '#991b1b', bg: '#fee2e2' },
    expired: { label: 'pozvánka expirovala', color: '#6b7280', bg: '#f3f4f6' },
    superseded: { label: 'nahradená novšou', color: '#6b7280', bg: '#f3f4f6' },
  };
  const fmtDt = (v?: string | null) => v ? new Date(v).toLocaleString('sk-SK', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const toggle = (key: string) => {
    setFeatures(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/auth/tenant/${tenant.slug}/features`, { features });
      if ((peppolRef || '') !== (tenant.peppol_customer_ref || '')) {
        await api.patch(`/auth/tenant/${tenant.slug}/peppol`, { customer_ref: peppolRef });
        tenant.peppol_customer_ref = peppolRef || null;
      }
      onUpdated(res.data.features);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Funkcie (Feature flagy)">
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
        Aktivuj alebo deaktivuj jednotlivé funkcie pre túto firmu. Zmeny sa prejavia po reload-e u prihlásených používateľov.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Jadro appky — nie je feature flag, vždy zapnuté. Zobrazené informatívne,
            aby zoznam modulov bol kompletný (user ho tu inak márne hľadal). */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1rem', backgroundColor: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
          <span style={{ flexShrink: 0, marginTop: '0.1rem', padding: '0.2rem 0.6rem', backgroundColor: '#16a34a', color: 'white', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600 }}>
            VŽDY ZAPNUTÉ
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, color: '#1f2937', marginBottom: '0.25rem' }}>Schvaľovanie faktúr (jadro)</div>
            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              Došlé doklady — upload PDF, emailová schránka, mobilný zber s QR, OCR, kontrola duplicít, schvaľovacie úrovne L1–L8 (schválenie / zamietnutie / vrátenie na opravu), auto-approve pravidlá a schvaľovacia pečiatka. Základ aplikácie, nedá sa vypnúť.
            </div>
          </div>
        </div>
        {FEATURE_DEFS.map(def => (
          <div key={def.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
            <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', flexShrink: 0, marginTop: '0.1rem' }}>
              <input type="checkbox" checked={features[def.key] === true} onChange={() => toggle(def.key)}
                style={{ opacity: 0, width: 0, height: 0 }} />
              <span style={{
                position: 'absolute', cursor: 'pointer', inset: 0,
                backgroundColor: features[def.key] ? '#16a34a' : '#cbd5e1',
                borderRadius: '24px', transition: 'background-color 0.2s'
              }}>
                <span style={{
                  position: 'absolute', height: '18px', width: '18px',
                  left: features[def.key] ? '22px' : '3px',
                  top: '3px', backgroundColor: 'white', borderRadius: '50%',
                  transition: 'left 0.2s'
                }} />
              </span>
            </label>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, color: '#1f2937', marginBottom: '0.25rem' }}>{def.label}</div>
              <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{def.description}</div>
            </div>
          </div>
        ))}
      </div>
      {features.peppol && (
        <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f5f3ff', borderRadius: '6px', border: '1px solid #ddd6fe' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#4c1d95', marginBottom: '0.35rem' }}>
            Peppol customerRef (ePošťák Connector)
          </label>
          <input value={peppolRef} onChange={e => setPeppolRef(e.target.value)} placeholder="napr. SF-MGTECH"
            style={{ width: '100%', maxWidth: 360, padding: '0.5rem 0.75rem', border: '1px solid #c4b5fd', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }} />
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.4rem' }}>
            Stabilné ID firmy v integrátorskom paneli poštára (Firmy → customerRef). Bez neho príjem ani odosielanie e-faktúr nebeží.
            Peppol ID firmy je vždy <code>0245:DIČ</code>.
          </div>

          <div style={{ marginTop: '1rem', paddingTop: '0.9rem', borderTop: '1px solid #ddd6fe' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4c1d95', marginBottom: '0.35rem' }}>
              Súhlas firmy u poštára
            </div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.6rem' }}>
              Poštár koná za firmu len s jej súhlasom. Vytvor pozvánku (podľa DIČ firmy: {tenant.dic || tenant.ico || '— chýba DIČ/IČO'}),
              odkaz pošli konateľovi — prihlási sa u poštára a súhlas potvrdí. Potom mu poštár firmu priradí k nášmu kľúču a doplníš customerRef vyššie.
            </div>
            {consent?.offer_id ? (
              <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                <span style={{ display: 'inline-block', padding: '0.15rem 0.6rem', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600,
                  color: CONSENT_LABELS[consent.status]?.color || '#374151', background: CONSENT_LABELS[consent.status]?.bg || '#f3f4f6' }}>
                  {CONSENT_LABELS[consent.status]?.label || consent.status}
                </span>
                <span style={{ marginLeft: 8, color: '#6b7280', fontSize: '0.78rem' }}>
                  vytvorená {fmtDt(consent.created_at)}
                  {consent.accepted_at && <> · potvrdená {fmtDt(consent.accepted_at)}</>}
                  {consent.revoked_at && <> · odvolaná {fmtDt(consent.revoked_at)}</>}
                  {!consent.accepted_at && consent.expires_at && <> · platí do {fmtDt(consent.expires_at)}</>}
                  {consent.checked_at && <> · overené {fmtDt(consent.checked_at)}</>}
                </span>
                {['issued', 'pending'].includes(consent.status) && consent.consent_url && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input readOnly value={consent.consent_url} onFocus={e => e.currentTarget.select()}
                      style={{ flex: 1, minWidth: 240, padding: '0.4rem 0.6rem', border: '1px solid #c4b5fd', borderRadius: 6, fontSize: '0.8rem', fontFamily: 'monospace' }} />
                    <button type="button" style={btnPrimary}
                      onClick={() => { navigator.clipboard?.writeText(consent.consent_url).then(() => alert('Odkaz skopírovaný — pošli ho konateľovi firmy.')); }}>
                      Kopírovať odkaz
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Firma zatiaľ nemá vytvorenú pozvánku na súhlas.</div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button type="button" onClick={createConsent} disabled={consentBusy}
                style={{ ...btnPrimary, backgroundColor: '#6d28d9' }}>
                {consentBusy ? 'Pracujem…' : consent?.offer_id ? 'Vytvoriť novú pozvánku' : 'Vytvoriť pozvánku na súhlas'}
              </button>
              {consent?.offer_id && (
                <button type="button" onClick={refreshConsent} disabled={consentBusy}
                  style={{ ...btnPrimary, backgroundColor: '#fff', color: '#4c1d95', border: '1px solid #c4b5fd' }}>
                  Overiť stav u poštára
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div style={{ marginTop: '1.5rem' }}>
        <button onClick={save} disabled={saving} style={{ ...btnPrimary, padding: '0.6rem 1.5rem' }}>
          {saving ? 'Ukladám...' : 'Uložiť features'}
        </button>
      </div>
    </Card>
  );
}

// ============================================================
// TAB: FLOWii (API key + manuálny sync trigger + audit log)
// ============================================================
function FlowiiTab({ tenant, onUpdated, showMsg }: { tenant: Tenant; onUpdated: () => void; showMsg: (m: string) => void }) {
  const [form, setForm] = useState({
    apiKey: '',
    username: tenant.flowii_username || '',
    password: '',
    companyId: tenant.flowii_company_id || '',
  });
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [runs, setRuns] = useState<any[]>([]);
  const flowiiEnabled = tenant.features?.flowii_integration === true;

  useEffect(() => {
    if (flowiiEnabled) loadRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id, flowiiEnabled]);

  const loadRuns = async () => {
    try {
      const res = await api.get('/flowii/sync');
      setRuns(res.data);
    } catch (e) {}
  };

  const saveCredentials = async () => {
    setSaving(true);
    try {
      await api.put(`/auth/tenant/${tenant.slug}`, {
        flowii_api_key: form.apiKey || undefined,
        flowii_username: form.username || undefined,
        flowii_password: form.password || undefined,
        flowii_company_id: form.companyId || undefined,
      });
      setForm({ ...form, apiKey: '', password: '' });
      onUpdated();
      showMsg('✓ FLOWii credentials uložené');
    } catch (err: any) {
      showMsg('✗ ' + (err.response?.data?.error || 'Chyba'));
    } finally {
      setSaving(false);
    }
  };

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const res = await api.post('/flowii/list-companies');
      setCompanies(res.data.companies);
      showMsg(`✓ Načítaných ${res.data.companies.length} firiem z FLOWii`);
    } catch (err: any) {
      showMsg('✗ ' + (err.response?.data?.error || 'Chyba'));
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSyncing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/flowii/upload-pohoda', formData);
      showMsg(`✓ Upload OK — ${res.data.total} faktúr (pridaných: ${res.data.added}, aktualizovaných: ${res.data.updated})`);
      setTimeout(loadRuns, 1500);
    } catch (err: any) {
      showMsg('✗ ' + (err.response?.data?.error || 'Chyba pri uploade'));
    } finally {
      setSyncing(false);
      e.target.value = ''; // reset input
    }
  };

  const checkPermissions = async () => {
    try {
      const res = await api.post('/flowii/permissions');
      const perms = res.data.permissions;
      const labels: Record<number, string> = { 1: 'Úlohy', 2: 'Partneri', 4: 'Udalosti', 11: 'Zákazky', 19: 'Obchodné prípady' };
      const text = perms.map((p: any) => `${labels[p.modul] || `Modul ${p.modul}`}: ${p.visible ? '✓' : '✗'}`).join('\n');
      alert(`FLOWii permissions:\n\n${text}\n\nPoznámka: Doklady nemajú vlastný modul v API permissions, ale 403 "Access restricted" pri exporte znamená že tvoj plán FLOWii pravdepodobne neumožňuje API export dokladov.`);
    } catch (err: any) {
      showMsg('✗ ' + (err.response?.data?.error || 'Chyba'));
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/flowii/sync');
      showMsg(`✓ Sync spustený (runId ${res.data.runId.slice(0, 8)}…). Obnovujem za 5s…`);
      setTimeout(loadRuns, 5000);
    } catch (err: any) {
      showMsg('✗ ' + (err.response?.data?.error || 'Chyba'));
    } finally {
      setSyncing(false);
    }
  };

  if (!flowiiEnabled) {
    return (
      <Card title="FLOWii integrácia">
        <p style={{ color: '#9ca3af' }}>
          Funkcia FLOWii Integration nie je zapnutá. Zapni ju v tabe <b>Features</b>.
        </p>
      </Card>
    );
  }

  const allReady = tenant.has_flowii_api_key && tenant.flowii_username && tenant.has_flowii_password && tenant.flowii_company_id;

  return (
    <Card title="FLOWii integrácia">
      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
        Sync sťahuje Pohoda XML export. Pre auth potrebujeme API key + username + heslo. Heslo a API key sú v DB <b>zašifrované</b> (AES-256-GCM).
      </p>

      <h3 style={h3}>Credentials</h3>
      <div style={grid2}>
        <Field
          label="API key"
          value={form.apiKey}
          onChange={(v) => setForm({ ...form, apiKey: v })}
          type="password"
          placeholder={tenant.has_flowii_api_key ? '••••••• (uložený)' : ''}
        />
        <Field
          label="FLOWii username"
          value={form.username}
          onChange={(v) => setForm({ ...form, username: v })}
          placeholder="email@firma.sk"
        />
        <Field
          label="FLOWii heslo"
          value={form.password}
          onChange={(v) => setForm({ ...form, password: v })}
          type="password"
          placeholder={tenant.has_flowii_password ? '••••••• (uložený)' : ''}
        />
        <div>
          <label style={lbl}>Company ID</label>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={form.companyId}
              onChange={(e) => setForm({ ...form, companyId: e.target.value })}
              placeholder="napr. 12345"
            />
            <button type="button" onClick={loadCompanies} disabled={loadingCompanies} style={{ ...btnNeutral, whiteSpace: 'nowrap' }}>
              {loadingCompanies ? '...' : 'Načítať firmy'}
            </button>
          </div>
          {companies.length > 0 && (
            <select style={{ ...inputStyle, marginTop: '0.4rem' }} value={form.companyId} onChange={(e) => setForm({ ...form, companyId: e.target.value })}>
              <option value="">— vyber —</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.id})</option>)}
            </select>
          )}
        </div>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <button onClick={saveCredentials} disabled={saving} style={{ ...btnPrimary, padding: '0.6rem 1.5rem' }}>
          {saving ? 'Ukladám...' : 'Uložiť credentials'}
        </button>
      </div>

      <h3 style={h3}>Manuálny upload Pohoda XML</h3>
      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
        Ak tvoj FLOWii plán nepodporuje API export, môžeš si v FLOWii UI stiahnuť Pohoda XML (alebo ZIP s viacerými XML)
        a nahrať ho sem. Faktúry sa naimportujú do našej DB.
      </p>
      <label style={{ display: 'inline-block', padding: '0.6rem 1.2rem', backgroundColor: '#16a34a', color: 'white', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem', opacity: syncing ? 0.6 : 1 }}>
        {syncing ? 'Spracúvam…' : '📤 Nahrať Pohoda XML / ZIP'}
        <input type="file" accept=".xml,.zip,application/xml,application/zip,text/xml"
          style={{ display: 'none' }}
          onChange={handleUpload}
          disabled={syncing}
        />
      </label>

      <h3 style={h3}>Automatický sync (vyžaduje FLOWii API plán)</h3>
      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
        Beží každú noc o 4:00. Prvý beh stiahne všetky výstupné faktúry od začiatku roka.
      </p>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={triggerSync} disabled={syncing || !allReady} style={{ ...btnPrimary, padding: '0.6rem 1.5rem', opacity: (syncing || !allReady) ? 0.6 : 1 }}>
          {syncing ? 'Spúšťam...' : 'Spustiť sync teraz'}
        </button>
        <button onClick={checkPermissions} disabled={!allReady} style={{ ...btnNeutral, padding: '0.6rem 1.5rem', opacity: !allReady ? 0.6 : 1 }}>
          🔍 Diagnostika permissions
        </button>
      </div>
      {!allReady && <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#dc2626' }}>Pred sync vyplň všetky credentials (API key, username, heslo, Company ID)</p>}

      <h3 style={h3}>História sync behov</h3>
      {runs.length === 0 ? (
        <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Žiadne sync behy zatiaľ.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              <th style={th}>Začatý</th>
              <th style={th}>Stav</th>
              <th style={th}>Pridané</th>
              <th style={th}>Aktualizované</th>
              <th style={th}>Chyba</th>
            </tr>
          </thead>
          <tbody>
            {runs.map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                <td style={td}>{new Date(r.started_at).toLocaleString('sk-SK')}</td>
                <td style={td}>
                  <span style={{
                    padding: '0.15rem 0.5rem',
                    backgroundColor: r.status === 'success' ? '#dcfce7' : r.status === 'error' ? '#fee2e2' : '#fef3c7',
                    color: r.status === 'success' ? '#16a34a' : r.status === 'error' ? '#dc2626' : '#b45309',
                    borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600,
                  }}>
                    {r.status}
                  </span>
                </td>
                <td style={td}>{r.invoices_added}</td>
                <td style={td}>{r.invoices_updated}</td>
                <td style={{ ...td, color: '#dc2626', fontSize: '0.8rem' }}>{r.error_message || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ============================================================
// TAB: Billing
// ============================================================
function BillingTab({ tenant, onUpdated }: { tenant: Tenant; onUpdated: (t: Tenant) => void }) {
  const [stats, setStats] = useState<{ users: number; documents: number; documents_this_month: number; months?: { month: string; documents: number; imported: number }[] } | null>(null);
  const [form, setForm] = useState({
    billing_plan: tenant.billing_plan,
    billing_model: tenant.billing_model,
    max_documents_per_month: tenant.max_documents_per_month || '',
    price_per_document: tenant.price_per_document != null ? String(Number(tenant.price_per_document)) : '',
  });
  const price = parseFloat(String(tenant.price_per_document ?? '')) || 0;
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [saving, setSaving] = useState(false);
  const [override, setOverride] = useState(!!tenant.billing_cap_override);
  const [ovrSaving, setOvrSaving] = useState(false);

  useEffect(() => {
    api.get(`/auth/tenant/${tenant.slug}/stats`).then(r => setStats(r.data)).catch(() => {});
  }, [tenant.slug]);

  const toggleOverride = async () => {
    const next = !override;
    setOvrSaving(true);
    try {
      await api.post(`/auth/tenant/${tenant.slug}/billing-override`, { override: next });
      setOverride(next);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba pri nastavení override');
    } finally {
      setOvrSaving(false);
    }
  };

  const limit = tenant.max_documents_per_month || 0;
  const overLimit = !!limit && !!stats && stats.documents_this_month >= limit;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/auth/tenant/${tenant.slug}`, {
        billing_plan: form.billing_plan,
        billing_model: form.billing_model,
        max_documents_per_month: form.max_documents_per_month ? parseInt(String(form.max_documents_per_month)) : null,
        price_per_document: form.price_per_document,
      });
      const p = form.price_per_document.replace(',', '.').trim();
      onUpdated({ ...tenant, billing_plan: form.billing_plan, billing_model: form.billing_model, max_documents_per_month: form.max_documents_per_month ? parseInt(String(form.max_documents_per_month)) : null, price_per_document: p === '' ? null : p });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Billing & limity">
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          <StatBox label="Používatelia" value={stats.users} />
          <StatBox label="Doklady celkom" value={stats.documents} />
          <StatBox label="Doklady tento mesiac" value={stats.documents_this_month} highlight={overLimit} />
        </div>
      )}

      {!!limit && (
        <div style={{
          padding: '0.85rem 1rem', marginBottom: '1.5rem', borderRadius: 6, fontSize: '0.85rem',
          background: overLimit && !override ? '#fef2f2' : override ? '#fffbeb' : '#f0fdf4',
          border: `1px solid ${overLimit && !override ? '#fecaca' : override ? '#fde68a' : '#bbf7d0'}`,
          color: overLimit && !override ? '#b91c1c' : override ? '#92400e' : '#15803d',
        }}>
          {override ? (
            <><strong>⚠ Override ZAPNUTÝ.</strong> Mesačný limit ({limit}) sa <strong>ignoruje</strong> — email schránka sa polluje bez obmedzenia.</>
          ) : overLimit ? (
            <><strong>⛔ Mesačný limit dosiahnutý ({stats?.documents_this_month}/{limit}).</strong> Email schránka sa <strong>NEpolluje</strong> — nové faktúry ostávajú v schránke neprečítané (nestratia sa). Natiahnu sa po novom mesiaci alebo zapnutí override.</>
          ) : (
            <>V limite: <strong>{stats?.documents_this_month ?? '—'}/{limit}</strong> dokladov tento mesiac. Po dosiahnutí limitu sa email polling pozastaví (maily počkajú neprečítané) a príde notifikácia adminovi.</>
          )}
          <div style={{ marginTop: '0.6rem' }}>
            <button onClick={toggleOverride} disabled={ovrSaving}
              style={{ padding: '0.45rem 1rem', borderRadius: 4, border: 'none', cursor: ovrSaving ? 'wait' : 'pointer',
                background: override ? '#92400e' : '#dc2626', color: 'white', fontWeight: 600, fontSize: '0.8rem' }}>
              {ovrSaving ? 'Ukladám…' : override ? 'Vypnúť override (obnoviť limit)' : 'Zapnúť override (ignorovať limit)'}
            </button>
          </div>
        </div>
      )}

      <div style={grid2}>
        <div>
          <label style={lbl}>Plán</label>
          <select style={inputStyle} value={form.billing_plan} onChange={e => setForm({ ...form, billing_plan: e.target.value })}>
            <option value="free">Free</option>
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Model fakturácie</label>
          <select style={inputStyle} value={form.billing_model} onChange={e => setForm({ ...form, billing_model: e.target.value })}>
            <option value="flat">Flat (mesačný paušál)</option>
            <option value="per_document">Per-doklad</option>
          </select>
        </div>
        <Field label="Max dokladov / mesiac (prázdne = bez limitu)" value={String(form.max_documents_per_month || '')} onChange={v => setForm({ ...form, max_documents_per_month: v })} type="number" />
        <Field label="Cena za doklad (€ bez DPH)" value={form.price_per_document} onChange={v => setForm({ ...form, price_per_document: v })} placeholder="napr. 0,03" hint="Pri modeli Per-doklad — počítadlo nižšie z nej ráta sumu." />
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <button onClick={save} disabled={saving} style={{ ...btnPrimary, padding: '0.6rem 1.5rem' }}>
          {saving ? 'Ukladám...' : 'Uložiť billing'}
        </button>
      </div>

      {/* Počítadlo prijatých dokladov po mesiacoch — podklad na fakturáciu per-doklad */}
      {!!stats?.months?.length && (
        <div style={{ marginTop: '2rem' }}>
          <h4 style={{ margin: '0 0 0.25rem', color: '#1f2937' }}>Počítadlo dokladov</h4>
          <p style={{ margin: '0 0 0.75rem', color: '#6b7280', fontSize: '0.8rem' }}>
            Všetko, čo prešlo cez OCR: prijaté doklady (aj zamietnuté) a vystavené faktúry naimportované z PDF. Zmazané sa nerátajú.
            {price ? ` Suma = doklady × ${price.toLocaleString('sk-SK', { maximumFractionDigits: 4 })} € bez DPH.` : ' Bez ceny za doklad — len počty.'}
          </p>
          <table style={{ width: '100%', maxWidth: 420, borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={th}>Mesiac</th>
                <th style={{ ...th, textAlign: 'right' }}>Prijaté</th>
                <th style={{ ...th, textAlign: 'right' }}>Import FA</th>
                {!!price && <th style={{ ...th, textAlign: 'right' }}>Suma</th>}
              </tr>
            </thead>
            <tbody>
              {stats.months.map(m => (
                <tr key={m.month} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={td}>{m.month}{m.month === thisMonth && <span style={{ color: '#9ca3af' }}> (prebieha)</span>}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{m.documents}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{m.imported || <span style={{ color: '#d1d5db' }}>0</span>}</td>
                  {!!price && <td style={{ ...td, textAlign: 'right' }}>{((m.documents + (m.imported || 0)) * price).toLocaleString('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ============================================================
// TAB: Pokročilé (delete)
// ============================================================
// B10: História zmien tenant polí. Backend zapisuje do tenant_audit_log
// pri každom UPDATE — zobraz najnovšie najprv. Slovenské popisky polí
// pomáhajú admin-novi rýchlo pochopiť čo sa menilo.
interface AuditEntry {
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  changed_at: string;
  changed_by: string | null;
  changed_by_user_id: string | null;  // users.id je UUID
}

const FIELD_LABEL_SK: Record<string, string> = {
  name: 'Názov firmy',
  slug: 'Slug',
  accounting_email: 'Účtovnícky email',
  email_inbox: 'Vstupná schránka',
  imap_host: 'IMAP host',
  imap_user: 'IMAP user',
  imap_port: 'IMAP port',
  imap_password: 'IMAP heslo',
  imap_issued_user: 'IMAP user (vystavené FA)',
  imap_issued_password: 'IMAP heslo (vystavené FA)',
  'features.resale_detection': 'Modul: Resale Detection',
  'features.flowii_integration': 'Modul: FLOWii',
  'features.omega_export': 'Modul: Omega Export',
  'features.finance_overview': 'Modul: Financie',
  'features.invoicing': 'Modul: Fakturácia',
  'features.orders': 'Modul: Objednávky',
  smtp_host: 'SMTP host',
  smtp_user: 'SMTP user',
  smtp_from: 'SMTP from',
  smtp_reply_to: 'Reply-To adresa',
  smtp_port: 'SMTP port',
  smtp_secure: 'SMTP secure',
  smtp_password: 'SMTP heslo',
  max_documents_per_month: 'Mesačný limit dokladov',
  price_per_document: 'Cena za doklad',
  billing_plan: 'Billing plán',
  billing_model: 'Billing model',
  active: 'Aktívna',
  telegram_chat_id: 'Telegram chat ID',
  telegram_bot_token: 'Telegram bot token',
  slack_bot_token: 'Slack bot token',
  flowii_api_key: 'FLOWii API key',
  flowii_password: 'FLOWii heslo',
};

// Feed z centrálneho activity_log — kto sa kedy prihlásil, čo nahral,
// schválil, vystavil, exportoval. Odpoveď na "čo tenant v appke robí".
interface ActivityEntry {
  event_type: string;
  entity_type: string | null;
  entity_id: string | null;
  source: string;
  metadata: Record<string, any> | null;
  created_at: string;
  user_label: string | null;
}

const ACTIVITY_LABEL_SK: Record<string, string> = {
  login: 'Prihlásenie',
  document_upload: 'Nahratý doklad',
  document_approved: 'Doklad schválený',
  document_rejected: 'Doklad zamietnutý',
  document_returned: 'Doklad vrátený na opravu',
  invoice_issue: 'Faktúra vystavená',
  invoice_send: 'Faktúra odoslaná',
  order_approved: 'Objednávka schválená',
  order_rejected: 'Objednávka zamietnutá',
  accounting_export: 'Export pre účtovníka',
};

const SOURCE_LABEL_SK: Record<string, string> = {
  web: 'web', email: 'email', capture: 'mobil', api: 'API', system: 'systém',
};

function ActivityTab({ slug }: { slug: string }) {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/auth/tenant/${slug}/activity`)
      .then(r => setEntries(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [slug]);

  // Stručný ľudský popis z metadát — bez JSON dumpu.
  const detail = (e: ActivityEntry): string => {
    const m = e.metadata || {};
    const parts: string[] = [];
    if (m.filename) parts.push(String(m.filename));
    if (m.invoice_number) parts.push(String(m.invoice_number));
    if (m.order_number) parts.push(String(m.order_number));
    if (m.vendor) parts.push(String(m.vendor));
    if (m.customer) parts.push(String(m.customer));
    if (m.amount != null) parts.push(`${m.amount} €`);
    if (m.to) parts.push(`→ ${m.to}`);
    if (m.month) parts.push(String(m.month));
    return parts.join(' · ');
  };

  if (loading) return <p style={{ color: '#6b7280' }}>Načítavam…</p>;

  return (
    <div>
      <h2 style={{ margin: '0 0 0.5rem', color: '#1f2937' }}>Aktivita firmy</h2>
      <p style={{ margin: '0 0 1rem', color: '#6b7280', fontSize: '0.875rem' }}>
        Posledné udalosti: prihlásenia, nahraté doklady, schválenia, faktúry a exporty. Staršie udalosti pred zavedením logu tu nie sú.
      </p>
      {entries.length === 0 ? (
        <div style={{ backgroundColor: '#f9fafb', borderRadius: '6px', padding: '1.5rem', textAlign: 'center', color: '#9ca3af' }}>
          Zatiaľ žiadna zaznamenaná aktivita.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.5rem 0.5rem 0', color: '#6b7280', fontWeight: 600 }}>Kedy</th>
              <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 600 }}>Kto</th>
              <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 600 }}>Udalosť</th>
              <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 600 }}>Detail</th>
              <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 600 }}>Zdroj</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '0.5rem 0.5rem 0.5rem 0', whiteSpace: 'nowrap', color: '#6b7280', fontSize: '0.8rem' }}>
                  {new Date(e.created_at).toLocaleString('sk-SK')}
                </td>
                <td style={{ padding: '0.5rem', color: '#374151' }}>{e.user_label || '‹systém›'}</td>
                <td style={{ padding: '0.5rem', color: '#1f2937', fontWeight: 500 }}>
                  {ACTIVITY_LABEL_SK[e.event_type] || e.event_type}
                </td>
                <td style={{ padding: '0.5rem', color: '#6b7280', wordBreak: 'break-word' }}>{detail(e)}</td>
                <td style={{ padding: '0.5rem', color: '#6b7280', fontSize: '0.8rem' }}>
                  {SOURCE_LABEL_SK[e.source] || e.source}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AuditTab({ slug }: { slug: string }) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterField, setFilterField] = useState('');

  useEffect(() => {
    api.get(`/auth/tenant/${slug}/audit`)
      .then(r => setEntries(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [slug]);

  const filtered = filterField
    ? entries.filter(e => e.field_name === filterField)
    : entries;
  const uniqueFields = Array.from(new Set(entries.map(e => e.field_name))).sort();

  const renderVal = (v: string | null) => {
    if (v === null || v === undefined) return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>;
    if (v === '<set>') return <span style={{ color: '#6b7280', fontStyle: 'italic' }}>‹nastavené›</span>;
    return <span style={{ color: '#1f2937' }}>{v}</span>;
  };

  if (loading) return <p style={{ color: '#6b7280' }}>Načítavam…</p>;

  return (
    <div>
      <h2 style={{ margin: '0 0 0.5rem', color: '#1f2937' }}>História zmien firmy</h2>
      <p style={{ margin: '0 0 1rem', color: '#6b7280', fontSize: '0.875rem' }}>
        Audit log zmien sledovaných polí. Citlivé heslá a tokeny sa zobrazujú len ako „nastavené" — skutočné hodnoty nikdy neopustia DB.
      </p>

      {entries.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ fontSize: '0.85rem', color: '#374151', marginRight: '0.5rem' }}>Filter pole:</label>
          <select value={filterField} onChange={e => setFilterField(e.target.value)}
            style={{ padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem' }}>
            <option value=''>(všetky)</option>
            {uniqueFields.map(f => (
              <option key={f} value={f}>{FIELD_LABEL_SK[f] || f}</option>
            ))}
          </select>
        </div>
      )}

      {filtered.length === 0 ? (
        <div style={{ backgroundColor: '#f9fafb', borderRadius: '6px', padding: '1.5rem', textAlign: 'center', color: '#9ca3af' }}>
          {entries.length === 0 ? 'Zatiaľ žiadne zmeny v audit logu.' : 'Pre vybraný filter žiadne zmeny.'}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.5rem 0.5rem 0', color: '#6b7280', fontWeight: 600 }}>Dátum</th>
              <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 600 }}>Pole</th>
              <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 600 }}>Pôvodná hodnota</th>
              <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 600 }}>Nová hodnota</th>
              <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 600 }}>Kto</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '0.5rem 0.5rem 0.5rem 0', whiteSpace: 'nowrap', color: '#6b7280', fontSize: '0.8rem' }}>
                  {new Date(e.changed_at).toLocaleString('sk-SK')}
                </td>
                <td style={{ padding: '0.5rem', color: '#374151', fontWeight: 500 }}>
                  {FIELD_LABEL_SK[e.field_name] || e.field_name}
                </td>
                <td style={{ padding: '0.5rem', wordBreak: 'break-all' }}>{renderVal(e.old_value)}</td>
                <td style={{ padding: '0.5rem', wordBreak: 'break-all' }}>{renderVal(e.new_value)}</td>
                <td style={{ padding: '0.5rem', color: '#6b7280', fontSize: '0.8rem' }}>
                  {e.changed_by || (e.changed_by_user_id ? `#${e.changed_by_user_id}` : '‹systém›')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DangerTab({ tenant, navigate }: { tenant: Tenant; navigate: ReturnType<typeof useNavigate> }) {
  const handleDeactivate = async () => {
    if (!window.confirm(`Naozaj deaktivovať "${tenant.name}"?\n\nDáta zostanú v DB, firma sa skryje. Môžeš ju neskôr obnoviť v zozname firiem (checkbox "Zobraziť aj deaktivované").`)) return;
    try {
      await api.delete(`/auth/tenant/${tenant.slug}`);
      navigate('/superadmin');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    }
  };

  const handleRestore = async () => {
    try {
      await api.post(`/auth/tenant/${tenant.slug}/restore`);
      window.location.reload();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    }
  };

  const [delConfirm, setDelConfirm] = useState(false);
  const [delPassword, setDelPassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleHardDelete = async () => {
    if (!delConfirm || !delPassword) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/auth/tenant/${tenant.slug}/hard`, {
        data: { confirm_slug: tenant.slug, password: delPassword },
      });
      alert(res.data?.message || 'Firma natrvalo vymazaná');
      navigate('/superadmin');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba pri trvalom mazaní');
      setDeleting(false);
    }
  };

  return (
    <Card title="Pokročilé" danger>
      <div style={{ padding: '1rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', marginBottom: '1rem' }}>
        <h3 style={{ margin: '0 0 0.5rem', color: '#dc2626' }}>{tenant.active ? 'Deaktivovať firmu' : 'Obnoviť firmu'}</h3>
        <p style={{ margin: '0 0 1rem', color: '#7f1d1d', fontSize: '0.875rem' }}>
          {tenant.active
            ? 'Firma sa skryje zo zoznamu. Dáta (používatelia, doklady, história) zostanú v DB. Akciu môžeš vrátiť späť.'
            : 'Firma je momentálne deaktivovaná. Klikni aby si ju vrátil späť do aktívneho zoznamu.'}
        </p>
        {tenant.active ? (
          <button onClick={handleDeactivate} style={{ padding: '0.6rem 1.5rem', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
            Deaktivovať firmu
          </button>
        ) : (
          <button onClick={handleRestore} style={{ padding: '0.6rem 1.5rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
            Obnoviť firmu
          </button>
        )}
      </div>

      <div style={{ padding: '1rem', backgroundColor: '#fef2f2', border: '2px solid #dc2626', borderRadius: '4px' }}>
        <h3 style={{ margin: '0 0 0.5rem', color: '#7f1d1d' }}>⚠ Vymazať natrvalo</h3>
        <p style={{ margin: '0 0 1rem', color: '#7f1d1d', fontSize: '0.875rem' }}>
          <strong>NENÁVRATNÉ.</strong> Natrvalo zmaže firmu <strong>{tenant.name}</strong> a
          VŠETKY jej dáta — používateľov, doklady, výstupné faktúry, pravidlá, históriu.
          Toto sa NEDÁ vrátiť späť (na rozdiel od deaktivácie).
        </p>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.75rem', color: '#7f1d1d', fontSize: '0.875rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={delConfirm} onChange={e => setDelConfirm(e.target.checked)} style={{ marginTop: '0.2rem' }} />
          <span>Rozumiem, že je to nenávratné a zmaže to všetky dáta firmy „{tenant.name}".</span>
        </label>
        <div style={{ maxWidth: 320, marginBottom: '0.75rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#7f1d1d', marginBottom: '0.25rem' }}>Tvoje heslo (super admin)</label>
          <input type="password" value={delPassword} onChange={e => setDelPassword(e.target.value)}
            placeholder="Zadaj svoje heslo"
            style={{ width: '100%', padding: '0.5rem', border: '1px solid #fecaca', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }} />
        </div>
        <button onClick={handleHardDelete} disabled={!delConfirm || !delPassword || deleting}
          style={{ padding: '0.6rem 1.5rem', backgroundColor: (!delConfirm || !delPassword || deleting) ? '#fca5a5' : '#7f1d1d', color: 'white', border: 'none', borderRadius: '4px', cursor: (!delConfirm || !delPassword || deleting) ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
          {deleting ? 'Mažem…' : 'Vymazať firmu natrvalo'}
        </button>
      </div>
    </Card>
  );
}

// ============================================================
// Helpers
// ============================================================
function Card({ title, children, danger }: { title: string; children: React.ReactNode; danger?: boolean }) {
  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: danger ? '1px solid #fecaca' : 'none' }}>
      <h2 style={{ margin: '0 0 1.5rem', color: danger ? '#dc2626' : '#1f2937', fontSize: '1.1rem' }}>{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type, placeholder, required, hint, fullWidth }: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string; required?: boolean; hint?: string; fullWidth?: boolean }) {
  return (
    <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
      <label style={lbl}>{label}{required && <span style={{ color: '#dc2626' }}> *</span>}</label>
      <input
        style={inputStyle}
        type={type || 'text'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
      />
      {hint && <p style={{ margin: '0.2rem 0 0', fontSize: '0.7rem', color: '#9ca3af' }}>{hint}</p>}
    </div>
  );
}

function StatBox({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div style={{ padding: '1rem', backgroundColor: highlight ? '#fee2e2' : '#f9fafb', borderRadius: '4px' }}>
      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 600, color: highlight ? '#dc2626' : '#1f2937' }}>{value}</div>
    </div>
  );
}

const roleLabel = (role: string): string => {
  const lvl = levelFromRole(role);
  if (lvl !== null) return levelRoleLabel(lvl);
  switch (role) {
    case 'admin': return 'Admin';
    case 'accountant': return 'Účtovník';
    case 'submitter': return 'Zberač dokladov';
    case 'viewer': return 'Pozorovateľ';
    default: return role;
  }
};

// ============================================================
// OmegaTab — per-tenant kódy evidencie a číselných radov v Omege
// ============================================================
interface OmegaCodes { evidence: string; sequence: string }
interface OmegaConfig {
  received_invoice: OmegaCodes;
  received_credit_note: OmegaCodes;
  issued_invoice: OmegaCodes;
  issued_credit_note: OmegaCodes;
}
const DEFAULT_OMEGA_CONFIG: OmegaConfig = {
  received_invoice:     { evidence: 'DF', sequence: 'DF' },
  received_credit_note: { evidence: 'DD', sequence: 'DD' },
  issued_invoice:       { evidence: 'OF', sequence: 'OF' },
  issued_credit_note:   { evidence: 'OD', sequence: 'OD' },
};

const OmegaTab: React.FC<{ tenant: any; onUpdated: () => void; showMsg: (m: string) => void }>
  = ({ tenant, onUpdated, showMsg }) => {
  const [cfg, setCfg] = useState<OmegaConfig>(() => {
    const t = tenant.omega_config || {};
    return {
      received_invoice:     { ...DEFAULT_OMEGA_CONFIG.received_invoice,     ...(t.received_invoice || {}) },
      received_credit_note: { ...DEFAULT_OMEGA_CONFIG.received_credit_note, ...(t.received_credit_note || {}) },
      issued_invoice:       { ...DEFAULT_OMEGA_CONFIG.issued_invoice,       ...(t.issued_invoice || {}) },
      issued_credit_note:   { ...DEFAULT_OMEGA_CONFIG.issued_credit_note,   ...(t.issued_credit_note || {}) },
    };
  });
  const [saving, setSaving] = useState(false);
  // B16: pravidlo účtovania dopravného (freight). mode: none/fixed/match_max
  const [freightMode, setFreightMode] = useState<'none' | 'fixed' | 'match_max'>(
    (tenant.omega_config?.freight?.mode) || 'none'
  );
  const [freightAccount, setFreightAccount] = useState<string>(
    tenant.omega_config?.freight?.account || ''
  );

  const updateField = (cat: keyof OmegaConfig, field: keyof OmegaCodes, value: string) => {
    setCfg(prev => ({ ...prev, [cat]: { ...prev[cat], [field]: value } }));
  };

  const save = async () => {
    if (freightMode === 'fixed' && !freightAccount.trim()) {
      alert('Pri pevnom účte dopravného zadaj číslo účtu (napr. 518.100).');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        ...cfg,
        freight: {
          mode: freightMode,
          account: freightMode === 'fixed' ? freightAccount.trim() : null,
        },
      };
      await api.patch(`/auth/tenant/${tenant.slug}/omega-config`, { omega_config: payload });
      onUpdated();
      showMsg('✓ Omega kódy uložené');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Chyba');
    } finally {
      setSaving(false);
    }
  };

  const rows: { key: keyof OmegaConfig; label: string; hint: string }[] = [
    { key: 'received_invoice',     label: 'Prijaté faktúry',       hint: 'Okruh DF v Omege' },
    { key: 'received_credit_note', label: 'Prijaté dobropisy',     hint: 'Okruh DD v Omege' },
    { key: 'issued_invoice',       label: 'Odoslané faktúry',      hint: 'Okruh OF v Omege' },
    { key: 'issued_credit_note',   label: 'Odoslané dobropisy',    hint: 'Okruh OD v Omege' },
  ];

  return (
    <Card title="Omega — kódy evidencie a číselných radov">
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem', lineHeight: 1.5 }}>
        Tieto kódy sa použijú pri Omega TXT exporte. V Omege ich nájdeš v Nastavenia → Číselovanie dokladov.
        Defaultné slovenské skratky stačí ponechať ak používaš štandardné kódy.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 12, color: '#374151' }}>Typ dokladu</th>
            <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 12, color: '#374151' }}>Kód evidencie</th>
            <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 12, color: '#374151' }}>Kód číselného radu</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.key} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '10px', fontSize: 13 }}>
                <div style={{ fontWeight: 500 }}>{r.label}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{r.hint}</div>
              </td>
              <td style={{ padding: '10px' }}>
                <input
                  value={cfg[r.key].evidence}
                  onChange={e => updateField(r.key, 'evidence', e.target.value)}
                  style={{ width: '120px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                />
              </td>
              <td style={{ padding: '10px' }}>
                <input
                  value={cfg[r.key].sequence}
                  onChange={e => updateField(r.key, 'sequence', e.target.value)}
                  style={{ width: '120px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* B16: účtovanie dopravného / poštovného */}
      <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Dopravné / poštovné</div>
        <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 10px', lineHeight: 1.5 }}>
          Ako sa pri prijatých faktúrach zaúčtuje položka dopravného (doprava, poštovné, balné…).
          V detaile faktúry sa dá účet vždy ručne prepísať.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {([
            { val: 'none', label: 'Žiadne špeciálne pravidlo', desc: 'Dopravné sa zaúčtuje ako bežná položka (účet z klasifikácie).' },
            { val: 'match_max', label: 'Podľa najdrahšej položky', desc: 'Dopravné dostane rovnaký účet ako najdrahšia položka faktúry.' },
            { val: 'fixed', label: 'Vždy na pevný účet', desc: 'Dopravné ide vždy na zadaný účet.' },
          ] as { val: 'none' | 'fixed' | 'match_max'; label: string; desc: string }[]).map(opt => (
            <label key={opt.val} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', border: `1px solid ${freightMode === opt.val ? '#2563eb' : '#e5e7eb'}`, borderRadius: 6, background: freightMode === opt.val ? '#eff6ff' : '#fff', cursor: 'pointer' }}>
              <input type="radio" name="freightMode" checked={freightMode === opt.val}
                onChange={() => setFreightMode(opt.val)} style={{ marginTop: 3 }} />
              <span>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{opt.label}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{opt.desc}</div>
              </span>
            </label>
          ))}
        </div>
        {freightMode === 'fixed' && (
          <div style={{ marginTop: 10 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 4 }}>Účet pre dopravné</label>
            <input
              value={freightAccount}
              onChange={e => setFreightAccount(e.target.value)}
              placeholder="napr. 518.100"
              style={{ width: '160px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
            />
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <button onClick={save} disabled={saving}
          style={{ padding: '0.6rem 1.4rem', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: saving ? 'wait' : 'pointer', fontWeight: 500, opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Ukladám…' : 'Uložiť'}
        </button>
      </div>
    </Card>
  );
};

const tabLabel = (t: Tab): string => {
  switch (t) {
    case 'general': return 'Základné údaje';
    case 'email': return 'Email';
    case 'slack': return 'Notifikácie';
    case 'users': return 'Používatelia';
    case 'logo': return 'Logo';
    case 'features': return 'Features';
    case 'flowii': return 'FLOWii';
    case 'omega': return 'Omega';
    case 'billing': return 'Billing';
    case 'activity': return 'Aktivita';
    case 'audit': return 'História zmien';
    case 'danger': return 'Pokročilé';
  }
};

const tabStyle = (active: boolean, danger: boolean): React.CSSProperties => ({
  padding: '0.75rem 1.25rem',
  backgroundColor: 'transparent',
  color: active ? (danger ? '#dc2626' : '#2563eb') : '#6b7280',
  border: 'none',
  borderBottom: active ? `2px solid ${danger ? '#dc2626' : '#2563eb'}` : '2px solid transparent',
  cursor: 'pointer',
  fontWeight: 500,
  fontSize: '0.9rem',
});

const th: React.CSSProperties = { padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500, fontSize: '0.85rem' };
const td: React.CSSProperties = { padding: '0.75rem 1rem', color: '#1f2937', fontSize: '0.9rem' };
const lbl: React.CSSProperties = { display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.85rem' };
const h3: React.CSSProperties = { margin: '1.5rem 0 1rem', color: '#1f2937', fontSize: '1rem' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' };
const grid2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' };
const btnPrimary: React.CSSProperties = { padding: '0.4rem 0.9rem', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' };
const btnNeutral: React.CSSProperties = { padding: '0.4rem 0.9rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' };
